import React, { useState, useEffect, useRef } from "react"
import { generateSlug, generateColor, colorOptions } from "../utils"

export default function AddNodeModal({
  isOpen,
  onClose,
  onCreateNew,
  onBulkCreate,
  onSelectExisting,
  categories,
  excludeIds,
  parentName,
  currentCategoryId,
  wouldCreateCycle,
}) {
  const [mode, setMode] = useState("new")
  const [bulkMode, setBulkMode] = useState(false)
  const [name, setName] = useState("")
  const [color, setColor] = useState(generateColor())
  const [bulkText, setBulkText] = useState("")
  const [search, setSearch] = useState("")
  const inputRef = useRef(null)

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 50)
  }, [isOpen, mode, bulkMode])

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && isOpen) onClose() }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const filtered = categories.filter(
    (c) =>
      !excludeIds.includes(c.id) &&
      !wouldCreateCycle(currentCategoryId, c.id) &&
      (c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.slug.toLowerCase().includes(search.toLowerCase()))
  )

  const handleCreate = () => {
    if (bulkMode) {
      const lines = bulkText.split("\n").map((l) => l.trim()).filter(Boolean)
      if (!lines.length) return
      const success = onBulkCreate(lines.map((n) => ({ name: n, color: generateColor() })))
      if (success) { setBulkText(""); onClose() }
    } else {
      if (!name.trim()) return
      const success = onCreateNew(name.trim(), color)
      if (success) { setName(""); setColor(generateColor()); onClose() }
    }
  }

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !bulkMode) { e.preventDefault(); handleCreate() }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleCreate() }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-[480px] max-h-[80vh] bg-admin_paper rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-admin_bordered"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-admin_bordered">
          <h3 className="text-admin_text font-semibold">Add to "{parentName}"</h3>
          <button
            onClick={onClose}
            className="text-admin_text_dim hover:text-admin_text transition-colors text-xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-admin_bordered">
          {["new", "existing"].map((t) => (
            <button
              key={t}
              onClick={() => setMode(t)}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                mode === t
                  ? "text-admin_primary border-b-2 border-admin_primary"
                  : "text-admin_text_dim hover:text-admin_text"
              }`}
            >
              {t === "new" ? "New Node" : "Existing"}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {mode === "new" ? (
            <>
              <label className="flex items-center gap-2 text-sm text-admin_text_dim cursor-pointer">
                <input
                  type="checkbox"
                  checked={bulkMode}
                  onChange={(e) => setBulkMode(e.target.checked)}
                  className="rounded"
                />
                Bulk mode
              </label>

              {bulkMode ? (
                <textarea
                  ref={inputRef}
                  placeholder="One node name per line..."
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  onKeyDown={onKeyDown}
                  rows={6}
                  className="w-full bg-admin_canvas border border-admin_bordered rounded-lg px-3 py-2 text-admin_text text-sm resize-none focus:outline-none focus:border-admin_primary"
                />
              ) : (
                <>
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Node name..."
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={onKeyDown}
                    className="w-full bg-admin_canvas border border-admin_bordered rounded-lg px-3 py-2 text-admin_text text-sm focus:outline-none focus:border-admin_primary"
                  />
                  {name && (
                    <p className="text-xs text-admin_text_dim">
                      Slug: <code className="text-admin_primary">{generateSlug(name)}</code>
                    </p>
                  )}
                  <div className="space-y-2">
                    <span className="text-xs text-admin_text_dim">Color</span>
                    <div className="flex flex-wrap gap-2">
                      {colorOptions.map((c) => (
                        <button
                          key={c}
                          onClick={() => setColor(c)}
                          style={{ backgroundColor: c }}
                          className={`w-6 h-6 rounded-full transition-transform ${
                            color === c ? "ring-2 ring-white ring-offset-1 ring-offset-admin_paper scale-110" : "hover:scale-105"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}

              <button
                onClick={handleCreate}
                disabled={bulkMode ? !bulkText.trim() : !name.trim()}
                className="w-full py-2 bg-admin_primary text-white rounded-lg text-sm font-medium disabled:opacity-40 hover:opacity-90 transition-opacity"
              >
                {bulkMode ? "+ Create Nodes" : "+ Create Node"}
              </button>
            </>
          ) : (
            <>
              <input
                ref={inputRef}
                type="text"
                placeholder="Search existing node..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-admin_canvas border border-admin_bordered rounded-lg px-3 py-2 text-admin_text text-sm focus:outline-none focus:border-admin_primary"
              />
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {filtered.length === 0 && (
                  <p className="text-center text-admin_text_dim text-sm py-4">
                    {categories.length <= 1 ? "No nodes yet" : "No nodes found"}
                  </p>
                )}
                {filtered.slice(0, 15).map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => { onSelectExisting(cat.id); onClose(); setSearch("") }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-admin_canvas text-left transition-colors"
                  >
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: cat.color }}
                    />
                    <span className="text-admin_text text-sm">{cat.name}</span>
                    <span className="text-admin_text_dim text-xs ml-auto">{cat.slug}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
